import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Ship, Plane, Truck, Train, Search, MapPin, ArrowRight, Loader2 } from 'lucide-react';
import { freightRouteService } from '@/features/master-data/services/freight-route-service';
import type { FreightRoute, TransportMode } from '@/features/master-data/types/freight-route.types';
import { FREIGHT_LEG_TYPE_LABELS } from '@/features/master-data/types/freight-route.types';
import { cn } from '@/lib/utils/cn';

const TRANSPORT_ICONS: Record<TransportMode, React.ElementType> = {
  SEA: Ship,
  AIR: Plane,
  LAND: Truck,
  RAIL: Train,
  MULTIMODAL: Truck,
};

interface LoadFreightRouteDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (route: FreightRoute) => void;
}

export function LoadFreightRouteDialog({ open, onClose, onSelect }: LoadFreightRouteDialogProps) {
  const [routes, setRoutes] = useState<FreightRoute[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setSearch('');
    setSelectedId(null);
    freightRouteService
      .getRoutes()
      .then((data) => setRoutes(data.filter((r) => r.isActive && r.legs.length > 0)))
      .catch(() => setRoutes([]))
      .finally(() => setLoading(false));
  }, [open]);

  const filtered = routes.filter((r) => {
    const q = search.toLowerCase();
    if (!q) return true;
    return (
      r.name.toLowerCase().includes(q) ||
      r.originLocation.toLowerCase().includes(q) ||
      r.destinationLocation.toLowerCase().includes(q)
    );
  });

  const selectedRoute = routes.find((r) => r.id === selectedId) ?? null;

  const handleConfirm = () => {
    if (!selectedRoute) return;
    onSelect(selectedRoute);
    onClose();
  };

  const formatCurrency = (v: number) =>
    `$${v.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Cargar Ruta de Flete</DialogTitle>
          <DialogDescription>
            Seleccione una ruta pre-configurada desde Datos Maestros. Los tramos se cargarán con sus
            precios actuales, que podrá editar después.
          </DialogDescription>
        </DialogHeader>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nombre, origen o destino..."
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Routes list */}
        <div className="flex-1 overflow-y-auto space-y-2 min-h-[200px] max-h-[400px]">
          {loading && (
            <div className="flex items-center justify-center py-10 text-muted-foreground gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Cargando rutas...
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <div className="text-center py-10 text-sm text-muted-foreground">
              {routes.length === 0
                ? 'No hay rutas de flete configuradas. Crealas primero en Datos Maestros › Tramos y tarifas.'
                : 'No se encontraron rutas que coincidan con la búsqueda.'}
            </div>
          )}

          {!loading &&
            filtered.map((route) => {
              const isSelected = selectedId === route.id;
              const activeLegs = route.legs.filter((l) => l.isActive);
              return (
                <button
                  key={route.id}
                  type="button"
                  className={cn(
                    'w-full text-left rounded-lg border p-3 transition-all hover:bg-muted/50',
                    isSelected && 'border-info bg-info/10 ring-1 ring-info/30'
                  )}
                  onClick={() => setSelectedId(isSelected ? null : route.id)}
                >
                  {/* Route header */}
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-medium text-sm">{route.name}</span>
                    <div className="flex-1" />
                    <Badge variant="outline" className="text-xs">
                      {activeLegs.length} tramo{activeLegs.length !== 1 ? 's' : ''}
                    </Badge>
                  </div>

                  {/* Origin → Destination */}
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2">
                    <MapPin className="h-3 w-3" />
                    <span>{route.originLocation}</span>
                    <ArrowRight className="h-3 w-3" />
                    <span>{route.destinationLocation}</span>
                  </div>

                  {/* Legs preview */}
                  <div className="space-y-1">
                    {activeLegs
                      .sort((a, b) => a.legOrder - b.legOrder)
                      .map((leg) => {
                        const Icon = TRANSPORT_ICONS[leg.transportMode];
                        const rate =
                          leg.ratePerCbm > 0
                            ? `${formatCurrency(leg.ratePerCbm)}/CBM`
                            : leg.ratePerKg > 0
                              ? `${formatCurrency(leg.ratePerKg)}/KG`
                              : 'Sin tarifa';
                        return (
                          <div
                            key={leg.id}
                            className="flex items-center gap-2 text-xs bg-muted/30 rounded px-2 py-1"
                          >
                            <Icon className="h-3 w-3 text-muted-foreground shrink-0" />
                            <span className="truncate">
                              {leg.origin} → {leg.destination}
                            </span>
                            <Badge variant="outline" className="text-[10px] shrink-0">
                              {FREIGHT_LEG_TYPE_LABELS[leg.legType]}
                            </Badge>
                            <div className="flex-1" />
                            <span className="text-ok font-medium shrink-0 tabular-nums">
                              {rate}
                            </span>
                          </div>
                        );
                      })}
                  </div>
                </button>
              );
            })}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleConfirm} disabled={!selectedRoute}>
            Cargar{' '}
            {selectedRoute ? `(${selectedRoute.legs.filter((l) => l.isActive).length} tramos)` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
